import { useContext, useEffect, useRef, type ReactNode } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import type { SmartBreadcrumbItem } from './BreadcrumbsBuilder';
import { BreadcrumbsContext } from './BreadcrumbsContext';

type UseBreadcrumbsBaseOptions = {
  id: string;
  enabled?: boolean;
  priority?: number;
};

type BreadcrumbSidebarLink = {
  label: string;
  href?: string;
};

type BreadcrumbSidebarGroup = {
  links: BreadcrumbSidebarLink[];
};

type BreadcrumbSidebarConfig = {
  groups: BreadcrumbSidebarGroup[];
};

type UseBreadcrumbsItemsMode = UseBreadcrumbsBaseOptions & {
  items: SmartBreadcrumbItem[];
  routeItems?: SmartBreadcrumbItem[];
  sidebarConfig?: BreadcrumbSidebarConfig;
};

type UseBreadcrumbsRouteMode = UseBreadcrumbsBaseOptions & {
  items?: SmartBreadcrumbItem[];
  routeItems: SmartBreadcrumbItem[];
  sidebarConfig?: BreadcrumbSidebarConfig;
};

type UseBreadcrumbsSidebarMode = UseBreadcrumbsBaseOptions & {
  items?: SmartBreadcrumbItem[];
  routeItems?: SmartBreadcrumbItem[];
  sidebarConfig: BreadcrumbSidebarConfig;
};

export type UseBreadcrumbsOptions =
  | UseBreadcrumbsItemsMode
  | UseBreadcrumbsRouteMode
  | UseBreadcrumbsSidebarMode;

type BreadcrumbsSnapshot = {
  id: string;
  items: SmartBreadcrumbItem[];
  enabled: boolean;
  priority: number;
};

function isPrimitiveLabel(label: ReactNode) {
  return (
    label == null ||
    typeof label === 'string' ||
    typeof label === 'number' ||
    typeof label === 'boolean'
  );
}

function areItemsEqual(a: SmartBreadcrumbItem[], b: SmartBreadcrumbItem[]) {
  if (a === b) {
    return true;
  }

  if (a.length !== b.length) {
    return false;
  }

  for (let index = 0; index < a.length; index += 1) {
    const aItem = a[index];
    const bItem = b[index];

    const labelMatches =
      isPrimitiveLabel(aItem.label) && isPrimitiveLabel(bItem.label)
        ? aItem.label === bItem.label
        : aItem.label === bItem.label;

    if (
      !labelMatches ||
      aItem.href !== bItem.href ||
      aItem.isCurrent !== bItem.isCurrent ||
      aItem.key !== bItem.key
    ) {
      return false;
    }
  }

  return true;
}

function normalizePath(path: string) {
  return path.split('?')[0].split('#')[0];
}

function sidebarConfigToRouteItems(
  sidebarConfig: BreadcrumbSidebarConfig | undefined,
) {
  if (!sidebarConfig) {
    return undefined;
  }

  const routeItems = sidebarConfig.groups.flatMap((group) =>
    group.links
      .filter((link) => Boolean(link.href))
      .map((link) => ({
        label: link.label,
        href: link.href,
      })),
  );

  return routeItems.length > 0 ? routeItems : undefined;
}

function resolveItemsForCurrentPath(
  pathname: string,
  items: SmartBreadcrumbItem[] | undefined,
  routeItems: SmartBreadcrumbItem[] | undefined,
) {
  const baseItems = items ?? [];
  if (!routeItems || routeItems.length === 0) {
    return baseItems;
  }

  const exactMatch = routeItems.find((routeItem) => {
    if (!routeItem.href) {
      return false;
    }
    return Boolean(
      matchPath(pathname, {
        path: normalizePath(routeItem.href),
        exact: true,
      }),
    );
  });

  const matchedRouteItem =
    exactMatch ??
    routeItems.reduce<SmartBreadcrumbItem | undefined>((best, routeItem) => {
      if (!routeItem.href) {
        return best;
      }
      const match = matchPath(pathname, {
        path: normalizePath(routeItem.href),
        exact: false,
      });
      if (!match) {
        return best;
      }
      if (!best?.href) {
        return routeItem;
      }
      return normalizePath(routeItem.href).length >
        normalizePath(best.href).length
        ? routeItem
        : best;
    }, undefined) ??
    routeItems[0];

  return [...baseItems, matchedRouteItem];
}

function areSnapshotsEqual(a: BreadcrumbsSnapshot, b: BreadcrumbsSnapshot) {
  return (
    a.id === b.id &&
    a.enabled === b.enabled &&
    a.priority === b.priority &&
    areItemsEqual(a.items, b.items)
  );
}

function useBreadcrumbsContext() {
  const context = useContext(BreadcrumbsContext);
  if (!context) {
    throw new Error('useBreadcrumbs must be used within BreadcrumbsProvider');
  }
  return context;
}

export function useBreadcrumbs({
  id,
  items,
  routeItems,
  sidebarConfig,
  enabled = true,
  priority = 0,
}: UseBreadcrumbsOptions) {
  const { setLayer, clearLayer } = useBreadcrumbsContext();
  const location = useLocation();
  const previousSnapshotRef = useRef<BreadcrumbsSnapshot | null>(null);
  const resolvedRouteItems =
    routeItems ?? sidebarConfigToRouteItems(sidebarConfig);
  const resolvedItems = resolveItemsForCurrentPath(
    location.pathname,
    items,
    resolvedRouteItems,
  );

  useEffect(() => {
    const currentSnapshot: BreadcrumbsSnapshot = {
      id,
      items: resolvedItems,
      enabled,
      priority,
    };
    const previousSnapshot = previousSnapshotRef.current;

    if (
      previousSnapshot &&
      areSnapshotsEqual(previousSnapshot, currentSnapshot)
    ) {
      return;
    }

    if (previousSnapshot && previousSnapshot.id !== id) {
      clearLayer(previousSnapshot.id);
    }

    if (!enabled) {
      clearLayer(id);
    } else {
      setLayer(id, resolvedItems, priority);
    }

    previousSnapshotRef.current = {
      ...currentSnapshot,
      items: [...resolvedItems],
    };
  }, [clearLayer, enabled, id, priority, resolvedItems, setLayer]);

  useEffect(
    () => () => {
      const previousSnapshot = previousSnapshotRef.current;
      if (!previousSnapshot) {
        return;
      }
      clearLayer(previousSnapshot.id);
    },
    [clearLayer],
  );
}

/** For internal use only! */
export function useMergedBreadcrumbs() {
  const { items } = useBreadcrumbsContext();
  return items;
}
